import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, img, errMsg, downloadCsv } from "@/lib/api";
import { fmtDate, money, TERMS, offerText } from "@/lib/format";
import { PageHead } from "@/components/portal/PortalShell";
import { NativeSelect, StatusBadge, Empty, CategoryIcon, Field } from "@/components/site/bits";

const useList = (url, params) => {
  const [items, setItems] = useState(null);
  const key = JSON.stringify(params);
  const load = () => api.get(url, { params }).then((r) => setItems(r.data));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, key]);
  return [items, load];
};

const Table = ({ head, rows, testid, empty = "Nothing here yet" }) =>
  !rows ? <div className="h-40 bg-slate-100 rounded-2xl animate-pulse" /> : rows.length === 0 ? <Empty title={empty} /> : (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
      <table className="w-full text-sm min-w-[720px]" data-testid={testid}>
        <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr>{head.map((h) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );

const StatusFilter = ({ value, onChange, options, testid }) => (
  <NativeSelect data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="!w-48 mb-4"><option value="">All statuses</option>{options.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}</NativeSelect>
);

export function AdminSubscriptions() {
  const [status, setStatus] = useState("");
  const [items] = useList("/admin/subscriptions", { status: status || undefined });
  const [reqs] = useList("/admin/requests", {});
  return (
    <div className="space-y-6">
      <PageHead title="Subscriptions" sub="Subscription status and payment status are tracked separately." />
      {reqs?.filter((r) => r.status === "open").length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-2" data-testid="open-requests">
          <p className="font-bold text-amber-900">Open activation / renewal requests</p>
          {reqs.filter((r) => r.status === "open").map((r) => <div key={r.id} className="text-sm flex justify-between"><span><b>{r.merchant_name}</b> · {r.package_name} · {TERMS[r.term]}</span><Link className="font-bold text-red-600" to={`/admin/merchants/${r.merchant_id}`}>Review</Link></div>)}
        </div>
      )}
      <StatusFilter testid="subs-status-filter" value={status} onChange={setStatus} options={["active", "suspended", "expired", "replaced"]} />
      <Table testid="subs-table" head={["Merchant", "Package", "Type", "Start", "Expires", "Status", "Payment", "Price"]} rows={items?.map((s) => (
        <tr key={s.id} className="border-t border-slate-100"><td className="p-3"><Link className="font-semibold hover:text-red-500" to={`/admin/merchants/${s.merchant_id}`}>{s.merchant_name}</Link></td><td className="p-3">{s.package_name} ({s.slots})</td><td className="p-3 text-xs">{TERMS[s.subscription_type] || s.subscription_type}</td><td className="p-3">{fmtDate(s.start_at, "Asia/Kolkata", false)}</td><td className="p-3">{fmtDate(s.expires_at, "Asia/Kolkata", false)}</td><td className="p-3"><StatusBadge status={s.status} /></td><td className="p-3"><StatusBadge status={s.payment_status} /></td><td className="p-3">{money(s.price)}</td></tr>
      ))} />
    </div>
  );
}

export function AdminDeals() {
  const [status, setStatus] = useState("");
  const [items, load] = useList("/admin/deals", { status: status || undefined });
  const act = async (d, action) => {
    try {
      await api.post(`/admin/deals/${d.id}/${action}`, { reason: action === "reject" ? window.prompt("Rejection reason?") || "" : "" });
      toast.success(`Deal ${action}d`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <PageHead title="Deals" sub="Moderate and manage all merchant deals." />
      <StatusFilter testid="admin-deals-filter" value={status} onChange={setStatus} options={["active", "pending_review", "paused", "draft", "expired", "archived", "rejected"]} />
      <Table testid="admin-deals-table" head={["Deal", "Merchant / Branch", "Offer", "Ends", "Status", "Claims", ""]} rows={items?.map((d) => (
        <tr key={d.id} className="border-t border-slate-100">
          <td className="p-3"><div className="flex items-center gap-2">{d.images?.[0] && <img src={img(d.images[0])} alt="" className="w-10 h-10 rounded-lg object-cover" />}<b>{d.title}</b></div></td>
          <td className="p-3">{d.merchant_name}<br /><span className="text-xs text-slate-500">{d.branch_name}</span></td><td className="p-3">{offerText(d)}</td><td className="p-3">{fmtDate(d.expires_at)}</td>
          <td className="p-3"><StatusBadge status={d.status} /></td><td className="p-3">{d.claims_count} / {d.redemptions_count}</td>
          <td className="p-3 space-x-1 whitespace-nowrap">
            {d.status === "pending_review" && <><button data-testid={`approve-deal-${d.id}`} onClick={() => act(d, "approve")} className="btn-act bg-emerald-600 text-white">Approve</button><button data-testid={`reject-deal-${d.id}`} onClick={() => act(d, "reject")} className="btn-act">Reject</button></>}
            {d.status === "active" && <button data-testid={`admin-pause-${d.id}`} onClick={() => act(d, "pause")} className="btn-act">Pause</button>}
            {d.status !== "archived" && <button data-testid={`admin-archive-${d.id}`} onClick={() => act(d, "archive")} className="btn-act">Archive</button>}
          </td>
        </tr>
      ))} />
    </div>
  );
}

export function AdminCategories() {
  const [items, load] = useList("/admin/categories", {});
  const [n, setN] = useState({ name: "", icon: "Tag" });
  const save = async (c, patch) => {
    try {
      await api.put(`/admin/categories/${c.id}`, { name: c.name, icon: c.icon, order: c.order, active: c.active, ...patch });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const move = async (i, dir) => {
    const a = items[i], b = items[i + dir];
    await save(a, { order: b.order });
    await save(b, { order: a.order });
  };
  const add = async () => {
    try {
      await api.post("/admin/categories", { ...n, order: (items?.length || 0) + 1, active: true });
      setN({ name: "", icon: "Tag" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="max-w-3xl">
      <PageHead title="Categories" sub="Rename, reorder, activate or add categories. Icons use Lucide names (e.g. Coffee, Gift)." />
      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
        {items?.map((c, i) => (
          <div key={c.id} className="flex items-center gap-3 p-3" data-testid={`category-row-${c.slug}`}>
            <CategoryIcon name={c.icon} className="w-5 h-5 text-red-500" />
            <Input defaultValue={c.name} onBlur={(e) => e.target.value !== c.name && save(c, { name: e.target.value })} className="flex-1" data-testid={`category-name-${c.slug}`} />
            <Input defaultValue={c.icon} onBlur={(e) => e.target.value !== c.icon && save(c, { icon: e.target.value })} className="w-32" />
            <button disabled={i === 0} onClick={() => move(i, -1)} className="p-1 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
            <button disabled={i === items.length - 1} onClick={() => move(i, 1)} className="p-1 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
            <Switch data-testid={`category-active-${c.slug}`} checked={c.active} onCheckedChange={(v) => save(c, { active: v })} />
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-4"><Input data-testid="new-category-name" placeholder="New category name" value={n.name} onChange={(e) => setN({ ...n, name: e.target.value })} /><Input placeholder="Icon" value={n.icon} onChange={(e) => setN({ ...n, icon: e.target.value })} className="w-32" /><button data-testid="add-category-button" onClick={add} className="px-5 rounded-md bg-red-500 text-white font-bold">Add</button></div>
    </div>
  );
}

export function AdminPackages() {
  const [items, load] = useList("/admin/packages", {});
  const save = async (p, k, v) => {
    try {
      const body = { name: p.name, slots: p.slots, activation_price: p.activation_price, monthly_price: p.monthly_price, six_month_price: p.six_month_price, annual_price: p.annual_price, initial_validity_months: p.initial_validity_months, active: p.active, [k]: v };
      await api.put(`/admin/packages/${p.id}`, body);
      toast.success("Package updated");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const cols = [["slots", "Slots"], ["activation_price", "Activation ₹"], ["monthly_price", "Monthly ₹"], ["six_month_price", "6 months ₹"], ["annual_price", "Annual ₹"], ["initial_validity_months", "Initial months"]];
  return (
    <div>
      <PageHead title="Packages" sub="Pricing changes apply to new activations and renewals." />
      <Table testid="packages-admin-table" head={["Package", ...cols.map((c) => c[1]), "Active"]} rows={items?.map((p) => (
        <tr key={p.id} className="border-t border-slate-100"><td className="p-3 font-bold">{p.name}</td>
          {cols.map(([k]) => <td key={k} className="p-2"><Input data-testid={`pkg-${p.code}-${k}`} type="number" defaultValue={p[k]} onBlur={(e) => +e.target.value !== p[k] && save(p, k, +e.target.value)} className="w-24" /></td>)}
          <td className="p-3"><Switch checked={p.active} onCheckedChange={(v) => save(p, "active", v)} /></td></tr>
      ))} />
    </div>
  );
}

export function AdminCustomers() {
  const [q, setQ] = useState("");
  const [items] = useList("/admin/customers", { q: q || undefined });
  return (
    <div>
      <PageHead title="Customers" />
      <Input data-testid="customer-search" placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs mb-4" />
      <Table testid="customers-table" head={["Name", "Email", "Mobile", "Joined", "Claims", "Redeemed"]} rows={items?.map((c) => (
        <tr key={c.id} className="border-t border-slate-100"><td className="p-3 font-semibold">{c.name}</td><td className="p-3">{c.email}</td><td className="p-3">{c.phone || "—"}</td><td className="p-3">{fmtDate(c.created_at, "Asia/Kolkata", false)}</td><td className="p-3">{c.claims}</td><td className="p-3">{c.redeemed}</td></tr>
      ))} />
    </div>
  );
}

export function AdminCoupons() {
  const [status, setStatus] = useState("");
  const [items] = useList("/admin/coupons", { status: status || undefined });
  return (
    <div>
      <PageHead title="Coupons" sub="Claimed, redeemed and expired coupons across the platform." />
      <StatusFilter testid="admin-coupons-filter" value={status} onChange={setStatus} options={["active", "redeemed", "expired", "cancelled"]} />
      <Table testid="admin-coupons-table" head={["Deal", "Merchant / Branch", "Customer", "Claimed", "Deadline", "Status", "Method"]} rows={items?.map((c) => (
        <tr key={c.id} className="border-t border-slate-100"><td className="p-3 font-semibold">{c.deal_title}</td><td className="p-3">{c.merchant_name}<br /><span className="text-xs text-slate-500">{c.branch_name}</span></td><td className="p-3">{c.customer_display}</td><td className="p-3">{fmtDate(c.claimed_at)}</td><td className="p-3">{fmtDate(c.deadline)}</td><td className="p-3"><StatusBadge status={c.status} /></td><td className="p-3 uppercase text-xs">{c.redemption_method || "—"}</td></tr>
      ))} />
    </div>
  );
}

export function AdminReports() {
  const run = (n) => downloadCsv(n).catch((e) => toast.error(errMsg(e)));
  const R = [["merchants", "Merchant performance & status"], ["deals", "Deal performance (views, claims, redemptions)"], ["coupons", "Coupon claims & redemptions"], ["subscriptions", "Subscription activations & expirations"], ["visits", "Verified store visits"]];
  return (
    <div className="max-w-2xl">
      <PageHead title="Reports" sub="Export CSV reports. Use the dashboard filters for period, city, merchant and category comparisons." />
      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
        {R.map(([k, l]) => <div key={k} className="flex items-center justify-between p-4"><span className="font-semibold text-sm">{l}</span><button data-testid={`export-${k}`} onClick={() => run(k)} className="btn-act"><Download className="w-3.5 h-3.5" />CSV</button></div>)}
      </div>
    </div>
  );
}

export function AdminAudit() {
  const [items] = useList("/admin/audit", {});
  return (
    <div>
      <PageHead title="Audit log" sub="Latest 500 administrative and security-relevant actions." />
      <Table testid="audit-table" head={["When", "Actor", "Action", "Entity", "Details"]} rows={items?.map((a) => (
        <tr key={a.id} className="border-t border-slate-100"><td className="p-3 whitespace-nowrap">{fmtDate(a.at)}</td><td className="p-3">{a.actor_email}<br /><span className="text-xs text-slate-500">{a.actor_role}</span></td><td className="p-3 font-mono text-xs">{a.action}</td><td className="p-3 text-xs">{a.entity}</td><td className="p-3 text-xs text-slate-500 max-w-xs truncate">{JSON.stringify(a.meta)}</td></tr>
      ))} />
    </div>
  );
}

export function AdminSettings() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data));
  }, []);
  if (!s) return null;
  const save = async () => {
    try {
      await api.put("/admin/settings", { deal_moderation: !!s.deal_moderation, support_email: s.support_email || "", support_phone: s.support_phone || "", subscription_alert_days: +s.subscription_alert_days || 7 });
      toast.success("Settings saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="max-w-2xl space-y-6">
      <PageHead title="Platform settings" />
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <label className="flex items-center justify-between"><span><b>Deal moderation</b><br /><span className="text-sm text-slate-500">When on, published deals wait for admin approval.</span></span><Switch data-testid="setting-moderation" checked={!!s.deal_moderation} onCheckedChange={(v) => setS({ ...s, deal_moderation: v })} /></label>
        <Field label="Support email"><Input value={s.support_email || ""} onChange={(e) => setS({ ...s, support_email: e.target.value })} /></Field>
        <Field label="Support phone"><Input value={s.support_phone || ""} onChange={(e) => setS({ ...s, support_phone: e.target.value })} /></Field>
        <button data-testid="settings-save" onClick={save} className="px-6 h-11 rounded-xl bg-red-500 text-white font-bold">Save settings</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-2" data-testid="payment-gateway-card">
        <h2 className="font-heading font-bold">Payment gateway</h2>
        <p className="text-sm">Status: <StatusBadge status={s.payment_gateway_enabled ? "active" : "paused"} testid="payment-gateway-status" /> {s.payment_gateway_enabled ? "Enabled" : "Disabled (PAYMENT_GATEWAY_ENABLED=false)"}</p>
        <p className="text-sm text-slate-500">The provider-independent payment layer is ready (orders, transactions, webhooks, invoices). It is controlled by a server environment flag and stays off during launch — merchants see "Request Activation" instead of Pay Now.</p>
      </div>
    </div>
  );
}
